import React, { useState, useMemo, useEffect } from "react";
import {
  FiMapPin,
  FiCalendar,
  FiMusic,
  FiUsers,
  FiShoppingBag,
  FiChevronLeft,
  FiChevronRight,
} from "react-icons/fi";
import { FaSpotify, FaYoutube, FaInstagram, FaFacebookF } from "react-icons/fa";
import MainContainer from "../../../components/utils/MainContainer";
import Particles from "../../../components/utils/Particles";
import {
  ActionBtn,
  BandActions,
  BandCard,
  BandContent,
  BandDescription,
  BandFooter,
  BandHeader,
  BandImage,
  BandInfo,
  BandName,
  BandsGrid,
  Container,
  ControlsSection,
  FilterTab,
  FilterTabs,
  GenreTag,
  InfoItem,
  MembersList,
  MembersSection,
  MembersTitle,
  MemberTag,
  NoResults,
  PageHeader,
  PageSubtitle,
  PageTitle,
  ProductCount,
  SearchIcon,
  SearchInput,
  SearchWrap,
  SocialBtn,
  SocialLinks,
  StatCard,
  StatLabel,
  StatNumber,
  StatsSection,
  Pagination,
  PageBtn,
  NavBtn,
  Ellipsis,
} from "./BandsPage.styles";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { selectBands } from "../../../store/slices/bands/slice";
import { getBands } from "../../../store/slices/bands/asyncThunks";

export default function BandsPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { data: bandsData, loading, pagination } = useSelector(selectBands);
  const [search, setSearch] = useState("");
  const [activeGenre, setActiveGenre] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setCurrentPage(1);
    dispatch(getBands({ page: 1, limit: 12 }));
  }, [dispatch]);

  const bandsList = useMemo(() => {
    if (!bandsData || !Array.isArray(bandsData)) {
      return [];
    }
    return bandsData;
  }, [bandsData]);

  // Generiši žanrove dinamički iz podataka o bendovima
  const genres = useMemo(() => {
    if (bandsList.length === 0) {
      return [{ id: "all", name: "Svi žanrovi" }];
    }

    // Izvuci unique žanrove iz bendova
    const uniqueGenres = [
      ...new Set(bandsList.map((band) => band.genre).filter(Boolean)),
    ];

    // Kreiraj genre objekte
    const genreOptions = uniqueGenres.map((genre) => ({
      id: genre.toLowerCase(),
      name: genre.charAt(0).toUpperCase() + genre.slice(1),
    }));

    // Dodaj "Svi žanrovi" na početak
    return [
      { id: "all", name: "Svi žanrovi" },
      ...[...genreOptions].sort((a, b) => a.name.localeCompare(b.name)),
    ];
  }, [bandsList]);

  // For pagination, use all bands from server (12 per page)
  // Filtering is applied on frontend for current page data
  const filteredBands = useMemo(() => {
    let filtered = bandsList;

    // Filtriranje po žanru
    if (activeGenre !== "all") {
      filtered = filtered.filter(
        (band) => band.genre && band.genre.toLowerCase() === activeGenre
      );
    }

    // Filtriranje po pretrazi
    if (search.trim()) {
      const searchQuery = search.toLowerCase().trim();
      filtered = filtered.filter((band) => {
        const nameMatch =
          band.name && band.name.toLowerCase().includes(searchQuery);
        const cityMatch =
          band.city && band.city.toLowerCase().includes(searchQuery);
        const descMatch =
          band.description &&
          band.description.toLowerCase().includes(searchQuery);
        const memberMatch =
          band.members &&
          band.members.some((member) =>
            (typeof member === "string" ? member : member.name || "")
              .toLowerCase()
              .includes(searchQuery)
          );

        return nameMatch || cityMatch || descMatch || memberMatch;
      });
    }

    return filtered;
  }, [bandsList, activeGenre, search]);

  const visibleBands = filteredBands;

  const bandsPerPage = 12;
  const totalPages =
    Math.ceil((pagination?.totalBands || 0) / bandsPerPage) || 1;

  const stats = useMemo(() => {
    return {
      totalBands: pagination?.totalBands || bandsList.length,
      totalProducts: bandsList.reduce(
        (sum, band) => sum + (band.products || 0),
        0
      ),
      genres: [...new Set(bandsList.map((band) => band.genre).filter(Boolean))]
        .length,
      cities: [...new Set(bandsList.map((band) => band.city).filter(Boolean))]
        .length,
    };
  }, [bandsList, pagination]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    dispatch(getBands({ page, limit: 12 }));
    // Scroll to top after page change
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 100);
  };

  const handleGenreChange = (genreId) => {
    setActiveGenre(genreId);
    setCurrentPage(1);
    // Reset search when changing genre
    setSearch("");
    dispatch(getBands({ page: 1, limit: 12 }));
  };

  // Effect to handle search changes
  useEffect(() => {
    if (search.trim()) {
      setCurrentPage(1);
    }
  }, [search]);

  // Check if filtering is active (which affects pagination)
  const isFiltering = activeGenre !== "all" || search.trim();

  // Pagination builder with ellipsis (smart)
  function renderPaginationBtns() {
    const btns = [];

    const pushPage = (n) =>
      btns.push(
        <PageBtn
          key={`p-${n}`}
          $active={n === currentPage}
          onClick={() => handlePageChange(n)}
        >
          {n}
        </PageBtn>
      );

    // Prev
    btns.push(
      <NavBtn
        key="prev"
        disabled={currentPage === 1}
        onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
      >
        <FiChevronLeft /> Prethodna
      </NavBtn>
    );

    // Always show 1
    pushPage(1);

    const delta = 2;
    const left = Math.max(2, currentPage - delta);
    const right = Math.min(totalPages - 1, currentPage + delta);

    if (left > 2) btns.push(<Ellipsis key="el1">…</Ellipsis>);

    for (let i = left; i <= right; i++) {
      if (i > 1 && i < totalPages) pushPage(i);
    }

    if (right < totalPages - 1) btns.push(<Ellipsis key="el2">…</Ellipsis>);

    if (totalPages > 1) pushPage(totalPages);

    // Next
    btns.push(
      <NavBtn
        key="next"
        disabled={currentPage === totalPages}
        onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
      >
        Sledeća <FiChevronRight />
      </NavBtn>
    );

    return btns;
  }

  // Loading state
  if (loading) {
    return (
      <MainContainer>
        <Particles />
        <Container>
          <PageHeader>
            <PageTitle>Naši bendovi</PageTitle>

            <PageSubtitle>
              Upoznajte bendove koji su deo naše zajednice. Otkrijte njihove
              priče, članove, diskografiju i pronađite originalnu merchandise
              robu.
            </PageSubtitle>
          </PageHeader>
          <div style={{ textAlign: "center", padding: "2rem", color: "#fff" }}>
            <l-line-spinner size="20" stroke="2" speed="1" color="#fff" />

            <p>Učitavanje bendova...</p>
          </div>
        </Container>
      </MainContainer>
    );
  }

  return (
    <MainContainer>
      <Particles />
      <Container>
        <PageHeader>
          <PageTitle>Naši bendovi</PageTitle>
          <PageSubtitle>
            Upoznajte bendove koji su deo naše zajednice. Otkrijte njihove
            priče, članove, diskografiju i pronađite originalnu merchandise
            robu.
          </PageSubtitle>
        </PageHeader>

        <StatsSection>
          <StatCard>
            <StatNumber>{stats.totalBands}</StatNumber>
            <StatLabel>Bendova</StatLabel>
          </StatCard>
          <StatCard>
            <StatNumber>{stats.totalProducts}</StatNumber>
            <StatLabel>Proizvoda</StatLabel>
          </StatCard>
          <StatCard>
            <StatNumber>{stats.genres}</StatNumber>
            <StatLabel>Žanrova</StatLabel>
          </StatCard>
          <StatCard>
            <StatNumber>{stats.cities}</StatNumber>
            <StatLabel>Gradova</StatLabel>
          </StatCard>
        </StatsSection>

        <ControlsSection>
          <SearchWrap>
            <SearchInput
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Pretraži bendove, članove, gradove..."
            />
            <SearchIcon />
          </SearchWrap>

          <FilterTabs>
            {genres.map((genre) => (
              <FilterTab
                key={genre.id}
                $active={activeGenre === genre.id}
                onClick={() => handleGenreChange(genre.id)}
              >
                {genre.name}
              </FilterTab>
            ))}
          </FilterTabs>
        </ControlsSection>

        {filteredBands.length === 0 ? (
          <NoResults>
            <h3>Nema rezultata</h3>
            <p>
              {bandsList.length === 0
                ? "Nema dostupnih bendova."
                : "Pokušajte sa drugim pojmom pretrage ili žanrom."}
            </p>
          </NoResults>
        ) : (
          <>
            <BandsGrid>
              {visibleBands.map((band) => {
                return (
                  <BandCard
                    onClick={() => {
                      navigate(`/bend/${band.id}`, {
                        state: { bandName: band.name },
                      });

                      setTimeout(() => {
                        window.scrollTo(0, 0);
                      }, 300);
                    }}
                    key={band.id || band.name}
                    layoutId={band.name}
                  >
                    <BandImage>
                      {(() => {
                        // Find main image from bandImages array
                        const mainImage = band.bandImages?.find(
                          (img) => img.isMain
                        );
                        const imageUrl = mainImage?.url || band.mainImage;

                        if (imageUrl) {
                          return (
                            <img
                              src={imageUrl}
                              alt={band.name}
                              className="band-main-image"
                              onError={(e) => {
                                // Fallback to text logo if image fails to load
                                e.target.style.display = "none";
                                e.target.nextElementSibling.style.display =
                                  "flex";
                              }}
                            />
                          );
                        } else {
                          return <div className="band-logo">{band.name}</div>;
                        }
                      })()}

                      {/* Fallback text logo (hidden by default if image exists) */}
                      <div
                        className="band-logo"
                        style={{
                          display:
                            band.bandImages?.find((img) => img.isMain)?.url ||
                            band.mainImage
                              ? "none"
                              : "flex",
                        }}
                      >
                        {band.name}
                      </div>

                      {/* Overlay content */}
                      <div className="band-image-overlay">
                        {band.genre && (
                          <GenreTag genre={band.genre}>
                            {band.genre.toUpperCase()}
                          </GenreTag>
                        )}
                        <ProductCount>
                          <FiShoppingBag size={14} />
                          {band.products || 0}
                        </ProductCount>
                      </div>
                    </BandImage>

                    <BandContent>
                      <BandHeader>
                        <BandName>{band.name}</BandName>
                      </BandHeader>

                      <BandInfo>
                        {band.founded && (
                          <InfoItem>
                            <FiCalendar />
                            <span>
                              <span className="label">Osnovan:</span>{" "}
                              {band.founded}
                            </span>
                          </InfoItem>
                        )}
                        {band.city && (
                          <InfoItem>
                            <FiMapPin />
                            <span>
                              <span className="label">Grad:</span> {band.city}
                            </span>
                          </InfoItem>
                        )}
                        {band.genre && (
                          <InfoItem>
                            <FiMusic />
                            <span>
                              <span className="label">Žanr:</span> {band.genre}
                            </span>
                          </InfoItem>
                        )}
                        {band.members && band.members.length > 0 && (
                          <InfoItem>
                            <FiUsers />
                            <span>
                              <span className="label">Članova:</span>{" "}
                              {band.members.length}
                            </span>
                          </InfoItem>
                        )}
                      </BandInfo>

                      {band.description && (
                        <BandDescription>{band.description}</BandDescription>
                      )}

                      {band.members && band.members.length > 0 && (
                        <MembersSection>
                          <MembersTitle>
                            <FiUsers />
                            Članovi benda
                          </MembersTitle>
                          <MembersList>
                            {band.members.map((member, memberIndex) => (
                              <MemberTag key={memberIndex}>
                                {member.name || member}
                              </MemberTag>
                            ))}
                          </MembersList>
                        </MembersSection>
                      )}

                      {/* <AlbumsSection>
                        <AlbumsTitle>
                          <FiDisc />
                          Diskografija
                        </AlbumsTitle>
                        {band.albums &&
                        band.albums.length > 0 &&
                        band.albums.some(
                          (album) => album.title && album.title.trim()
                        ) ? (
                          <AlbumsList>
                            {band.albums
                              .filter(
                                (album) => album.title && album.title.trim()
                              )
                              .slice(0, 4)
                              .map((album, albumIndex) => (
                                <AlbumItem key={albumIndex}>
                                  <span className="album-name">
                                    {album.title}
                                  </span>
                                  <span className="album-year">
                                    {album.year}
                                  </span>
                                </AlbumItem>
                              ))}
                          </AlbumsList>
                        ) : (
                          <div
                            style={{
                              color: "#999",
                              fontSize: "0.9rem",
                              fontStyle: "italic",
                              textAlign: "center",
                              padding: "1rem 0",
                            }}
                          >
                            Nema dostupne diskografije
                          </div>
                        )}
                      </AlbumsSection> */}

                      <BandFooter>
                        {(band.fbUrl ||
                          band.instUrl ||
                          band.spotifyUrl ||
                          band.ytUrl) && (
                          <SocialLinks>
                            {band.spotifyUrl && (
                              <SocialBtn
                                onClick={() =>
                                  window.open(band.spotifyUrl, "_blank")
                                }
                                title="Spotify"
                              >
                                <FaSpotify />
                              </SocialBtn>
                            )}
                            {band.ytUrl && (
                              <SocialBtn
                                onClick={() =>
                                  window.open(band.ytUrl, "_blank")
                                }
                                title="YouTube"
                              >
                                <FaYoutube />
                              </SocialBtn>
                            )}
                            {band.instUrl && (
                              <SocialBtn
                                onClick={() =>
                                  window.open(band.instaUrl, "_blank")
                                }
                                title="Instagram"
                              >
                                <FaInstagram />
                              </SocialBtn>
                            )}
                            {band.fbUrl && (
                              <SocialBtn
                                onClick={() =>
                                  window.open(band.fbUrl, "_blank")
                                }
                                title="Facebook"
                              >
                                <FaFacebookF />
                              </SocialBtn>
                            )}
                          </SocialLinks>
                        )}

                        <BandActions>
                          <ActionBtn
                            $primary
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/prodavnica?bend=${band.id}`);
                              setTimeout(() => {
                                window.scrollTo(0, 0);
                              }, 300);
                            }}
                          >
                            <FiShoppingBag />
                            Shop
                          </ActionBtn>
                        </BandActions>
                      </BandFooter>
                    </BandContent>
                  </BandCard>
                );
              })}
            </BandsGrid>

            {!isFiltering && totalPages > 1 && (
              <Pagination>{renderPaginationBtns()}</Pagination>
            )}

            {isFiltering && filteredBands.length > 0 && (
              <div
                style={{
                  textAlign: "center",
                  marginTop: "2rem",
                  color: "#cccccc",
                  fontStyle: "italic",
                }}
              >
                Prikazano {filteredBands.length} rezultata od ukupno{" "}
                {pagination?.totalBands || 0} bendova
              </div>
            )}
          </>
        )}
      </Container>
    </MainContainer>
  );
}
